import { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useParams, useSearchParams } from "react-router-dom";

import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetShipmentInvoices } from "api/queries/invoice/useGetShipmentInvoices";
import Badge, { BadgeColor } from "components/v2/Badge/Badge";
import usePermissions from "hooks/usePermissions";
import { formatNumber } from "helpers/commonHelpers";
import testIds from "constants/testIds";

const INVOICE_STATUS_TO_BADGE_COLOR: Record<string, BadgeColor> = {
  "Awaiting Audit": "warning",
  Approved: "success",
  Declined: "error",
  Unsubmitted: "gray",
  Removed: "gray",
};

function TopNavBillingPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const isShipper = user_type === "shipper";

  const { data: shipmentBasicData } = useGetShipmentBasic(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { data: invoices = [] } = useGetShipmentInvoices({
    shipmentId: id!,
    options: {
      enabled: !!id,
      select: (data: any) => data.results,
      refetchOnMount: false,
    },
  }) as any;

  const [totalQuoted, setTotalQuoted] = useState("");
  const [totalInvoiced, setTotalInvoiced] = useState("");
  const [invoicedPercentageDifference, setInvoicedPercentageDifference] = useState(0);

  const { approved_by, approved_by_email, shipper_name, payment_location } = shipmentBasicData?.shipment || {};

  const currentInvoiceId = searchParams.get("invoice");
  const currentInvoice = useMemo(
    () => invoices?.find((invoice: any) => invoice.invoice_id === currentInvoiceId),
    [currentInvoiceId, invoices],
  );
  const isCustomsInvoice = currentInvoice?.invoice_type === "customs_broker_invoice";

  useEffect(() => {
    if (invoices.length) {
      let totalInvoicedAmount = 0;
      let totalQuotedAmount = 0;
      let totalQuoteCurrencyCode = "";
      let totalInvoicedCurrencyCode = "";

      invoices
        .filter((item: any) => item.invoice_status !== "Removed" && item.invoice_status !== "Unsubmitted")
        .forEach((item: any) => {
          const { total_quoted_amount, expected_amount_due, total_quoted_amount_currency, expected_amount_due_currency } =
            item?.financial;
          totalInvoicedAmount += expected_amount_due;
          totalQuotedAmount = total_quoted_amount;
          totalQuoteCurrencyCode = total_quoted_amount_currency;
          totalInvoicedCurrencyCode = expected_amount_due_currency;
        });

      setTotalQuoted(`${totalQuoteCurrencyCode}`);
      setTotalInvoiced(`${totalInvoicedCurrencyCode}`);

      // Store raw values for display
      setTotalQuotedAmount(totalQuotedAmount);
      setTotalInvoicedAmount(totalInvoicedAmount);

      const percentageDiff = ((totalInvoicedAmount - totalQuotedAmount) / totalQuotedAmount) * 100;
      setInvoicedPercentageDifference(Number(percentageDiff.toFixed(1)));
    }
  }, [invoices]);

  const [totalQuotedAmount, setTotalQuotedAmount] = useState(0);
  const [totalInvoicedAmount, setTotalInvoicedAmount] = useState(0);

  if (!invoices.length && !shipmentBasicData) return null;

  const billStatus = (() => {
    if (!invoices.length) return null;
    const earliestInvoice = invoices.reduce(
      (a: any, b: any) =>
        a.invoice_references?.invoice_submit_datetime < b.invoice_references?.invoice_submit_datetime ? a : b,
      invoices[0],
    );
    return earliestInvoice?.invoice_status;
  })();

  const showPercentageBadge =
    invoicedPercentageDifference !== 0 &&
    invoicedPercentageDifference !== -100 &&
    isFinite(invoicedPercentageDifference) &&
    !isNaN(invoicedPercentageDifference);

  return (
    <div className="topnav-v2__details topnav-v2__details--billing-grid" data-testid={testIds.TOPNAV_V2_BILLING_DETAILS}>
      {/* Bill status */}
      {billStatus && (
        <div className="topnav-v2__billing-status-col" data-testid={testIds.TOPNAV_V2_BILLING_STATUS_CONTAINER}>
          <span className="topnav-v2__detail-label" data-testid={testIds.TOPNAV_V2_BILLING_STATUS_LABEL}>
            <FormattedMessage id="tracking.components.statusCard.statusHeader" defaultMessage="Bill status" />
          </span>
          <div className="w-fit" data-testid={testIds.TOPNAV_V2_BILLING_STATUS_VALUE}>
            <Badge
              color={INVOICE_STATUS_TO_BADGE_COLOR[billStatus] || "gray"}
              type="status-badge"
              size="sm"
              label={billStatus}
            />
          </div>
        </div>
      )}

      {/* Total Invoiced */}
      {invoices.length > 0 && (
        <div className="topnav-v2__detail-col" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_INVOICED_CONTAINER}>
          <span className="topnav-v2__detail-label" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_INVOICED_LABEL}>
            <FormattedMessage id="invoices.totalInvoiced" defaultMessage="Total invoiced" />
          </span>
          <div className="topnav-v2__billing-amount-row" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_INVOICED_ROW}>
            {showPercentageBadge && (
              <Badge
                color={invoicedPercentageDifference > 0 ? "warning" : "success"}
                type="status-badge"
                size="sm"
                label={`${invoicedPercentageDifference > 0 ? "+" : ""}${invoicedPercentageDifference}%`}
              />
            )}
            <div className="topnav-v2__billing-amount" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_INVOICED_VALUE}>
              <span className="topnav-v2__billing-currency" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_INVOICED_CURRENCY}>
                {totalInvoiced}
              </span>
              <span className="topnav-v2__billing-value" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_INVOICED_AMOUNT}>
                {formatNumber(totalInvoicedAmount, 2)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Total Quoted */}
      {invoices.length > 0 && !isCustomsInvoice && (
        <div className="topnav-v2__detail-col" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_QUOTED_CONTAINER}>
          <span className="topnav-v2__detail-label" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_QUOTED_LABEL}>
            <FormattedMessage id="invoices.totalQuoted" defaultMessage="Total quoted" />
          </span>
          <div className="topnav-v2__billing-amount" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_QUOTED_VALUE}>
            <span className="topnav-v2__billing-currency" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_QUOTED_CURRENCY}>
              {totalQuoted}
            </span>
            <span className="topnav-v2__billing-value" data-testid={testIds.TOPNAV_V2_BILLING_TOTAL_QUOTED_AMOUNT}>
              {formatNumber(totalQuotedAmount, 2)}
            </span>
          </div>
        </div>
      )}

      {/* Shipment created by */}
      {shipper_name && (
        <div className="topnav-v2__detail-col" data-testid={testIds.TOPNAV_V2_BILLING_CREATED_BY_CONTAINER}>
          <span className="topnav-v2__detail-label" data-testid={testIds.TOPNAV_V2_BILLING_CREATED_BY_LABEL}>
            <FormattedMessage id="invoices.shipmentCreatedBy" defaultMessage="Shipment created by" />
          </span>
          <div className="topnav-v2__billing-user" data-testid={testIds.TOPNAV_V2_BILLING_CREATED_BY_VALUE}>
            <span className="topnav-v2__billing-user-name" data-testid={testIds.TOPNAV_V2_BILLING_CREATED_BY_NAME}>
              {shipper_name}
            </span>
            {payment_location && (
              <span className="topnav-v2__billing-user-team" data-testid={testIds.TOPNAV_V2_BILLING_CREATED_BY_SUBTITLE}>
                {payment_location}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Shipment approved by */}
      {isShipper && approved_by && (
        <div className="topnav-v2__detail-col" data-testid={testIds.TOPNAV_V2_BILLING_APPROVED_BY_CONTAINER}>
          <span className="topnav-v2__detail-label" data-testid={testIds.TOPNAV_V2_BILLING_APPROVED_BY_LABEL}>
            <FormattedMessage id="invoices.shipmentApprovedBy" defaultMessage="Shipment approved by" />
          </span>
          <div className="topnav-v2__billing-user" data-testid={testIds.TOPNAV_V2_BILLING_APPROVED_BY_VALUE}>
            {approved_by_email ? (
              <a
                href={`mailto:${approved_by_email}`}
                className="topnav-v2__billing-user-name"
                data-testid={testIds.TOPNAV_V2_BILLING_APPROVED_BY_EMAIL_LINK}
              >
                {approved_by}
              </a>
            ) : (
              <span className="topnav-v2__billing-user-name" data-testid={testIds.TOPNAV_V2_BILLING_APPROVED_BY_NAME}>
                {approved_by}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TopNavBillingPage;
